import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
} from '@mui/material';
import { ImportExport as DioIcon } from '@mui/icons-material';
import { CABO_PADROES_TUBO_LOOSE, PadraoTuboLoose } from '../../services/caboService';

export const DIO_PORT_OPTIONS = [12, 24, 36, 48, 60, 72, 84, 96, 108, 120, 144] as const;

export interface DioAddConfig {
  portas: number;
  padrao_cores: PadraoTuboLoose;
}

interface DioAddModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (config: DioAddConfig) => void;
  isFullscreen?: boolean;
  container?: HTMLElement | null;
}

const DioAddModal: React.FC<DioAddModalProps> = ({
  open,
  onClose,
  onConfirm,
  isFullscreen = false,
  container,
}) => {
  const [portas, setPortas] = useState<number>(12);
  const [padraoCores, setPadraoCores] = useState<PadraoTuboLoose>('abnt');

  const menuZIndex = isFullscreen ? 10006 : 1600;
  const selectMenuProps = {
    MenuProps: { disablePortal: true, sx: { zIndex: menuZIndex } },
  };
  const selectFormControlSx = { '& .MuiPopover-root': { zIndex: menuZIndex } };

  useEffect(() => {
    if (open) {
      setPortas(12);
      setPadraoCores('abnt');
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm({ portas, padrao_cores: padraoCores });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      container={isFullscreen && container ? container : undefined}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
      sx={{ zIndex: isFullscreen ? 10005 : 1500 }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DioIcon sx={{ color: '#FF5722' }} />
          <Typography variant="h6" component="span" sx={{ fontSize: '1rem' }}>
            Adicionar DIO
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Distribuidor Interno Óptico — cada porta de entrada conecta à saída do mesmo número.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small" required sx={selectFormControlSx}>
            <InputLabel>Padrão de cores</InputLabel>
            <Select
              label="Padrão de cores"
              value={padraoCores}
              onChange={(e) => setPadraoCores(e.target.value as PadraoTuboLoose)}
              {...selectMenuProps}
            >
              {CABO_PADROES_TUBO_LOOSE.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Cores das fibras em cada grupo de 12 portas</FormHelperText>
          </FormControl>

          <FormControl fullWidth size="small" required sx={selectFormControlSx}>
            <InputLabel>Quantidade de portas</InputLabel>
            <Select
              label="Quantidade de portas"
              value={portas}
              onChange={(e) => setPortas(Number(e.target.value))}
              {...selectMenuProps}
            >
              {DIO_PORT_OPTIONS.map((qtd) => (
                <MenuItem key={qtd} value={qtd}>
                  {qtd} portas ({qtd / 12} grupo{qtd > 12 ? 's' : ''} de 12)
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Múltiplos de 12 (12 a 144)</FormHelperText>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleConfirm}>
          Adicionar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DioAddModal;
